/**
 * Service da Área do PCP (Legado, ADR-0087): manuais editáveis e execuções
 * em checklist (`/api/area-pcp`).
 *
 * Cada função recebe `request` (do useAuth) como 1º argumento e devolve o
 * JSON da resposta. `silentError`: quem chama mostra a mensagem do backend
 * (AsyncButton, aviso no formulário ou a fila offline); sem isso sairiam dois
 * avisos iguais. O erro lançado traz o `detalhe` em `err.message`, o código em
 * `err.status` e o corpo em `err.payload` (o 409 de identificador repetido
 * manda a execução que já existe em `payload.existente`). Sem rede o erro vem
 * com `err.code` NETWORK ou TIMEOUT.
 */
const PREFIXO = "/api/area-pcp";

const id = (valor) => encodeURIComponent(String(valor));

function ler(request, caminho) {
  return request(`${PREFIXO}${caminho}`, { silentError: true });
}

function escrever(request, caminho, corpo, metodo = "POST") {
  return request(`${PREFIXO}${caminho}`, {
    method: metodo,
    body: JSON.stringify(corpo ?? {}),
    silentError: true,
  });
}

/** True quando a falha foi de rede (sem resposta do servidor), não de regra. */
export function ehFalhaDeRede(erro) {
  return erro?.code === "NETWORK" || erro?.code === "TIMEOUT";
}

// ------------------------------------------------------------------ manuais

/** `{tarefas, pode_editar, pode_executar}`; `apagadas`: a lixeira (só editor). */
export function listarTarefas(request, { apagadas = false } = {}) {
  return ler(request, `/tarefas${apagadas ? "?apagadas=1" : ""}`);
}

export function lerTarefa(request, tarefaId) {
  return ler(request, `/tarefas/${id(tarefaId)}`);
}

/** Tarefa nova (nasce rascunho, versão 1) → TAREFA. */
export function criarTarefa(request, conteudo) {
  return escrever(request, "/tarefas", conteudo);
}

/** Salva o manual inteiro como versão nova. 409 = outra pessoa salvou antes. */
export function salvarTarefa(request, tarefaId, conteudo, versaoBase) {
  return escrever(request, `/tarefas/${id(tarefaId)}`, { ...conteudo, versao_base: versaoBase }, "PUT");
}

export function definirAtivo(request, tarefaId, ativo) {
  return escrever(request, `/tarefas/${id(tarefaId)}/ativar`, { ativo });
}

export function apagarTarefa(request, tarefaId) {
  return escrever(request, `/tarefas/${id(tarefaId)}/apagar`);
}

export function restaurarTarefa(request, tarefaId) {
  return escrever(request, `/tarefas/${id(tarefaId)}/restaurar`);
}

/** `itens`: `[{id, ativo}]` de TODAS as tarefas fora da lixeira, na nova ordem. */
export function organizarTarefas(request, itens) {
  return escrever(request, "/tarefas/organizar", { itens });
}

export function listarVersoes(request, tarefaId) {
  return ler(request, `/tarefas/${id(tarefaId)}/versoes`);
}

export function lerVersao(request, tarefaId, numero) {
  return ler(request, `/tarefas/${id(tarefaId)}/versoes/${id(numero)}`);
}

export function restaurarVersao(request, tarefaId, numero, versaoBase) {
  return escrever(request, `/tarefas/${id(tarefaId)}/versoes/${id(numero)}/restaurar`, { versao_base: versaoBase });
}

/** `{pausa: [{id, texto, ativo}], cancelamento: [...]}`. */
export function listarMotivos(request) {
  return ler(request, "/motivos");
}

export function salvarMotivos(request, tipo, itens) {
  return escrever(request, "/motivos", { tipo, itens }, "PUT");
}

/** `{resultados, total, tarefas}`; ignora acentos. */
export function buscarNosManuais(request, termo) {
  return ler(request, `/busca?q=${encodeURIComponent(termo)}`);
}

// ------------------------------------------------------------------ execuções

export function listarHistorico(request, filtros = {}) {
  const params = new URLSearchParams();
  Object.entries(filtros).forEach(([chave, valor]) => {
    if (valor) params.set(chave, valor);
  });
  const consulta = params.toString();
  return ler(request, `/execucoes/historico${consulta ? `?${consulta}` : ""}`);
}

export function lerExecucao(request, execucaoId) {
  return ler(request, `/execucoes/${id(execucaoId)}`);
}

/** `{tarefa_id, identificador, id}` → EXECUÇÃO (201). 409 traz `payload.existente`. */
export function iniciarExecucao(request, corpo) {
  return escrever(request, "/execucoes", corpo);
}

/** Lote de eventos (online ou da fila offline) → EXECUÇÃO + `{rejeitados, conflitos}`. */
export function enviarEventos(request, execucaoId, eventos, dispositivo) {
  return escrever(request, `/execucoes/${id(execucaoId)}/eventos`, { eventos, dispositivo });
}

/** `{total}` de execuções paradas há mais de 1 dia útil, para o sino. */
export function lerResumoAreaPcp(request) {
  return ler(request, "/pendencias-resumo");
}

/** Manuais ativos + execuções abertas + motivos: o que o aparelho guarda offline. */
export function lerPacoteOffline(request) {
  return ler(request, "/offline/pacote");
}
